import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, API } from "../../lib/api";
import { StatCard, EmpowermentBar } from "../../components/bits";
import { InspirationBanner } from "../../components/InspirationBanner";
import { School, Users, UserCheck, Clock, Coins, Sparkles, Medal, FileDown, Swords, Briefcase } from "lucide-react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rperiod, setRperiod] = useState("monthly");
  const [gender, setGender] = useState("");
  const [authority, setAuthority] = useState("");
  const [stage, setStage] = useState("");

  useEffect(() => {
    setData(null);
    const params = new URLSearchParams();
    if (gender) params.set("gender", gender);
    if (authority) params.set("authority", authority);
    if (stage) params.set("stage", stage);
    api.get(`/admin/overview?${params}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [gender, authority, stage]);

  if (!data) return <div className="text-center text-mutext py-20 font-head">جارٍ تحميل لوحة الإدارة …</div>;

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <InspirationBanner />

      <div data-testid="stage-filter" className="flex gap-2 flex-wrap items-center">
        <span className="text-[11px] font-bold text-mutext">المرحلة:</span>
        {[["", "كل المراحل"], ["ثانوية", "الثانوية"], ["متوسطة", "المتوسطة"], ["ابتدائية", "الابتدائية"], ["روضة", "رياض الأطفال"]].map(([val, label]) => (
          <button key={val || "all"} data-testid={`dash-stage-${val || "all"}`} onClick={() => setStage(val)}
            className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${
              stage === val ? "bg-turq-600 text-white shadow-lg shadow-turq-600/25" : "bg-surface text-mutext border border-brd hover:border-turq-600"}`}>
            {label}
          </button>
        ))}
      </div>

      <div data-testid="gender-filter" className="flex gap-2 flex-wrap">
        {[["", "جميع المدارس"], ["بنين", "مدارس البنين"], ["بنات", "مدارس البنات"]].map(([val, label]) => (
          <button key={val || "all"} data-testid={`gender-${val || "all"}`} onClick={() => setGender(val)}
            className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${
              gender === val ? "bg-turq-900 text-white shadow-lg shadow-turq-900/25" : "bg-surface text-mutext border border-brd hover:border-turq-600"}`}>
            {label}
          </button>
        ))}
      </div>

      <div data-testid="authority-filter" className="flex gap-2 flex-wrap items-center">
        <span className="text-[11px] font-bold text-mutext">السلطة:</span>
        {[["", "كل المدارس"], ["حكومي", "حكومي"], ["أهلي", "أهلي"], ["عالمي وأجنبي", "عالمي وأجنبي"]].map(([val, label]) => (
          <button key={val || "all"} data-testid={`authority-${val || "all"}`} onClick={() => setAuthority(val)}
            className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${
              authority === val ? "bg-coral-600 text-white shadow-lg shadow-coral-600/25" : "bg-surface text-mutext border border-brd hover:border-coral-600"}`}>
            {label}
          </button>
        ))}
      </div>

      {data.by_authority && (
        <div className="grid grid-cols-3 gap-3" data-testid="authority-breakdown">
          {Object.entries(data.by_authority).map(([a, g]) => (
            <div key={a} data-testid={`authority-card-${a}`} className="bg-surface rounded-2xl border border-brd shadow-md p-4 text-center hover-lift">
              <div className="text-[11px] font-bold text-mutext mb-1">{a}</div>
              <div className="font-head font-black text-lg text-turq-900 dark:text-turq-300">{g.schools} مدارس</div>
              <div className="text-[10px] text-mutext">{g.students} طالب · {g.volunteers} متطوع · <b className="text-coral-600">{g.pct}%</b></div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={School} label="مدرسة مشاركة" value={data.schools} testid="stat-schools" />
        <StatCard icon={Users} label="إجمالي الطلاب" value={data.students} tone="beige" testid="stat-students" />
        <StatCard icon={UserCheck} label="متطوع فريد" value={data.volunteers} testid="stat-volunteers" />
        <StatCard icon={Briefcase} label="موظفون متطوعون" value={data.staff_volunteers} tone="beige" testid="stat-staff-volunteers" />
        <StatCard icon={Clock} label="ساعة تطوعية" value={data.hours} tone="coral" testid="stat-hours" />
        <StatCard icon={Coins} label="العائد الاقتصادي (ر.س)" value={data.economic_value} tone="coral" testid="stat-economic" />
        <StatCard icon={Sparkles} label="فرصة تطوعية" value={data.opportunities} testid="stat-opportunities" />
        <StatCard icon={Users} label="منسق مدرسة" value={data.coordinators} tone="beige" testid="stat-coordinators" />
        <StatCard icon={Medal} label="شهادة صادرة" value={data.certificates} testid="stat-certificates" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { key: "total", title: "الإجمالي (بنين + بنات)", theme: "bg-gradient-to-l from-[#D8C9AE] to-[#F0E6D2] dark:from-surface2 dark:to-surface2", chip: "bg-coral-900" },
          { key: "بنين", title: "مدارس البنين", theme: "bg-turq-100 dark:bg-surface2", chip: "bg-turq-900" },
          { key: "بنات", title: "مدارس البنات", theme: "bg-coral-100 dark:bg-surface2", chip: "bg-coral-600" },
        ].map((panel) => {
          const g = panel.key === "total" ? data : data.by_gender[panel.key];
          return (
            <div key={panel.key} data-testid={`group-panel-${panel.key}`} className={`rounded-3xl overflow-hidden shadow-md border border-brd/50 ${panel.theme}`}>
              <div className={`${panel.chip} text-white px-4 py-2.5 flex items-center justify-between gap-2`}>
                <b className="font-head text-[13px]">{panel.title}</b>
                <span className="text-[10px] font-bold bg-white/25 rounded-full px-2.5 py-0.5 whitespace-nowrap">تحقيق المستهدف {g.pct}%</span>
              </div>
              <div className="grid grid-cols-3 gap-2 p-3">
                {[[g.schools, "المدارس"], [g.students, "عدد الطلاب"], [g.volunteers, "المتطوعون الفعليون"],
                  [g.opportunities, "الفرص"], [g.hours, "الساعات المنفذة"], [`${g.economic_value}`, "العائد (ر.س)"]].map(([v, l]) => (
                  <div key={l} className="bg-surface/85 dark:bg-surface rounded-xl p-2.5 text-center">
                    <div className="font-head font-black text-sm">{v}</div>
                    <div className="text-[9px] text-mutext leading-tight mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <EmpowermentBar pct={data.pct} volunteers={data.volunteers} total={data.students} target={90} testid="admin-empowerment-bar" />

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-surface rounded-3xl p-6 shadow-md border border-brd/50">
          <h3 className="font-head font-bold text-sm mb-4">مؤشر التمكين حسب الشرائح الثلاث (المستهدف 90%)</h3>
          <div className="space-y-3">
            {data.tiers.map((t) => (
              <div key={t.tier} data-testid={`tier-row-${t.tier}`} className="bg-bg rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[13px] font-bold">{t.info.name}</div>
                    <div className="text-[10.5px] text-mutext">{t.schools} مدارس · {t.unique} فريد من {t.students} طالب · وزن ×{t.info.weight}</div>
                  </div>
                  <span className={`font-head font-black text-lg ${t.pct >= 90 ? "text-turq-600" : "text-coral-600"}`}>{t.pct}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-surface2 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(100, t.pct)}%`, background: t.pct >= 90 ? "#12939E" : "#FF7A59" }} />
                </div>
              </div>
            ))}
            {data.staff_tiers?.some((t) => t.schools > 0) && (
              <div data-testid="staff-tiers-block" className="mt-6 pt-5 border-t border-brd">
                <h3 className="font-head font-bold text-sm mb-3">منافسة المنسوبين — المراحل الأخرى (روضة / ابتدائية / متوسطة)</h3>
                <div className="space-y-3">
                  {data.staff_tiers.map((t) => (
                    <div key={t.tier} data-testid={`staff-tier-row-${t.tier}`} className="bg-bg rounded-2xl p-4">
                      <div className="text-[13px] font-bold">{t.info.name}</div>
                      <div className="text-[10.5px] text-mutext">{t.schools} مدارس · {t.unique} منسوب مشارك من {t.staff} · {t.hours} ساعة · وزن ×{t.info.weight}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link to="/admin/schools" data-testid="view-schools-link" className="block text-center text-xs font-bold text-turq-600 hover:text-turq-900 mt-4">
            عرض مصفوفة المدارس الكاملة ←
          </Link>
        </div>

        <div className="space-y-5">
          <div className="bg-surface rounded-3xl p-6 shadow-md border border-brd/50">
            <h3 className="font-head font-bold text-sm mb-4">التقرير العام الشهري</h3>
            <div className="flex gap-3 items-end flex-wrap">
              <select data-testid="general-report-period" value={rperiod} onChange={(e) => setRperiod(e.target.value)}
                className="rounded-xl border border-brd bg-surface px-3 py-2.5 text-sm outline-none">
                <option value="monthly">شهري</option>
                <option value="quarterly">فصلي</option>
                <option value="annual">سنوي (غلاف رسمي)</option>
              </select>
              {rperiod === "monthly" && (
                <input data-testid="general-report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                  className="flex-1 min-w-[130px] rounded-xl border border-brd bg-surface px-3 py-2.5 text-sm outline-none" dir="ltr" />
              )}
              <button data-testid="general-report-download"
                onClick={() => window.open(`${API}/admin/reports/general?month=${month}&period=${rperiod}`, "_blank")}
                className="rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-turq-600 hover:bg-turq-900 transition-colors flex items-center gap-2">
                <FileDown className="w-4 h-4" /> تقرير عام (PDF)
              </button>
            </div>
          </div>

          {data.active_challenge && (
            <div data-testid="admin-active-challenge" className="rounded-3xl p-6 bg-turq-100 dark:bg-surface2 border border-turq-300/40 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-turq-900 flex items-center justify-center shrink-0">
                <Swords className="w-5 h-5 text-white" />
              </div>
              <div>
                <b className="font-head text-sm block">{data.active_challenge.title}</b>
                <span className="text-xs text-mutext">{data.active_challenge.participants?.length || 0} منسقاً مشاركاً · حتى {data.active_challenge.week_end}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
